<template>
  <div class="p-6 space-y-4 animate-fade-in">
    <h2 class="text-white text-xl font-semibold">Picker</h2>
    <p class="text-slate-400 text-sm">Select from available inventory</p>

    <div v-if="error" class="text-red-400 text-sm">{{ error }}</div>

    <div class="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-6">
      <div v-for="t in types" :key="t.type_key" class="panel-neo panel-neo--glassier panel-neo--no-sheen p-5">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-white font-semibold">{{ t.name }}</div>
            <div class="text-slate-400 text-xs">{{ t.make }} • {{ t.model }}</div>
            <div class="text-slate-500 text-xs mt-1">{{ t.descriptor }}</div>
          </div>
          <button class="px-2 py-1 rounded bg-primary-600 text-white" @click="open(t)">Pick</button>
        </div>
        <div class="mt-3 text-xs text-slate-300">Available: {{ t.available_count }}</div>
      </div>
    </div>

    <!-- Backdrop and modal with Motion timing to avoid flash/blur issues -->
    <Motion
      v-if="active"
      class="fixed inset-0 backdrop-blur bg-black/40 z-40"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      :exit="{ opacity: 0 }"
      :transition="{ duration: 0.28 }"
      @click="close"
    />
    <div v-if="active" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <Motion
        class="glass-strong rounded-xl p-5 w-full max-w-xl relative z-50"
        :initial="{ opacity: 0, y: 16, scale: 0.96 }"
        :animate="{ opacity: 1, y: 0, scale: 1 }"
        :exit="{ opacity: 0, y: 12, scale: 0.96 }"
        :transition="{ duration: 0.5 }"
      >
        <div class="text-white font-semibold mb-2">Select Serial (Available)</div>
        <div class="max-h-[50vh] overflow-auto divide-y divide-slate-800">
          <div
            v-for="s in serials"
            :key="s.serial"
            class="py-2 flex items-center justify-between text-slate-200"
          >
            <span class="font-mono text-xs">{{ s.serial }}</span>
            <button class="px-2 py-1 rounded bg-primary-600 text-white" @click="choose(s)">
              Choose
            </button>
          </div>
        </div>
        <div class="mt-4 text-right">
          <button
            class="px-3 py-1 rounded bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700"
            @click="close"
          >
            Close
          </button>
        </div>
      </Motion>
    </div>

    <!-- Decision & flow modals -->
    <DecisionModal
      v-if="decision && !showReserve && !showMaintenance && !showRoadmap && !showShipping"
      :make="decision.make"
      :model="decision.model"
      :serial="decision.serial"
      @close="decision = null"
      @reserve="onReserve"
      @checkout="onCheckout"
      @maintenance="onMaintenance"
    />

    <ReserveModal
      v-if="showReserve && decision"
      :equipment-id="decision.equipmentId"
      @close="handleReserveClose"
      @saved="handleReserveClose"
    />

    <MaintenanceFormModal
      v-if="showMaintenance && decision"
      :make="decision.make"
      :model="decision.model"
      :serial="decision.serial"
      @close="handleMaintenanceClose"
      @saved="handleMaintenanceClose"
    />

    <RoadmapModal
      v-if="showRoadmap && decision"
      :make="decision.make"
      :model="decision.model"
      :serial="decision.serial"
      status="reserved"
      @close="showRoadmap = false"
    />
    <ShippingLabelModal
      v-if="showShipping && decision"
      :make="decision.make"
      :model="decision.model"
      :serial="decision.serial"
      :checkout-id="activeCheckoutId ?? undefined"
      @close="handleShippingClose"
      @saved="handleShippingClose"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import { Motion } from 'motion-v'
import DecisionModal from '@/components/modals/DecisionModal.vue'
import ReserveModal from '@/components/modals/ReserveModal.vue'
import MaintenanceFormModal from '@/components/modals/MaintenanceFormModal.vue'
import RoadmapModal from '@/components/modals/RoadmapModal.vue'
import ShippingLabelModal from '@/components/modals/ShippingLabelModal.vue'
import { createLogger } from '@/lib/logger'

const log = createLogger('Picker')
const DATA_UPDATED_EVENT = 'testdrive:data-updated'
const types = ref<any[]>([])
const error = ref('')
const active = ref<any | null>(null)
const serials = ref<any[]>([])
const decision = ref<{ make: string; model: string; serial: string; equipmentId: number } | null>(null)
const showReserve = ref(false)
const showMaintenance = ref(false)
const showRoadmap = ref(false)
const showShipping = ref(false)
/** Checkout ID created during the checkout flow, passed to ShippingLabelModal */
const activeCheckoutId = ref<number | null>(null)

function resetDecision() {
  decision.value = null
  activeCheckoutId.value = null
}

function handleReserveClose() {
  showReserve.value = false
  resetDecision()
}

function handleShippingClose() {
  showShipping.value = false
  resetDecision()
}

function onMaintenance() {
  showMaintenance.value = true
}

function handleMaintenanceClose() {
  showMaintenance.value = false
  resetDecision()
}


async function load() {
  const { data, error: err } = await supabase
    .from('equipment_types_v')
    .select('*')
    .gt('available_count', 0)
    .order('name')
  if (err) {
    error.value = err.message
    return
  }
  types.value = data || []
}

async function open(t: any) {
  active.value = t
  const { data } = await supabase
    .from('equipment_serials_v')
    .select('serial, equipment_type_id')
    .eq('make', t.make)
    .eq('model', t.model)
    .eq('status', 'available')
    .order('serial')
  serials.value = data || []
}

function close() {
  active.value = null
}

function onDataUpdatedEvent() {
  void load()
}

onMounted(() => {
  void load()
  window.addEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
})

onBeforeUnmount(() => {
  window.removeEventListener(DATA_UPDATED_EVENT, onDataUpdatedEvent)
})

function choose(row: { serial: string; equipment_type_id: number }) {
  if (!active.value) return
  // Reset flow: only show decision initially
  showReserve.value = false
  showRoadmap.value = false
  showShipping.value = false
  decision.value = {
    make: (active.value as any).make,
    model: (active.value as any).model,
    serial: row.serial,
    equipmentId: row.equipment_type_id,
  }
  active.value = null
}

function onReserve() {
  showReserve.value = true
}

/**
 * Create a checkout record for this equipment serial, then show
 * the Roadmap + Shipping Label modals with the checkoutId linked.
 */
async function onCheckout() {
  if (!decision.value) return
  error.value = ''
  try {
    // Resolve equipment_serial_id from the serial string
    const { data: serialRow } = await supabase
      .from('equipment_serials_v')
      .select('id, equipment_type_id')
      .eq('serial', decision.value.serial)
      .limit(1)
      .maybeSingle()

    if (serialRow) {
      // Look up the reservation for this serial to get contact/org info
      const { data: resRow } = await supabase
        .from('reservations_v')
        .select('id, equipment_id, organization_name, contact_name')
        .eq('equipment_serial', decision.value.serial)
        .order('start_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      // Create checkout record
      const { data: co, error: coErr } = await supabase
        .schema('app')
        .from('checkouts')
        .insert({
          equipment_serial_id: serialRow.id,
          equipment_id: resRow?.equipment_id ?? serialRow.equipment_type_id,
          status: 'checked-out',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single()
      if (coErr) {
        log.warn('checkout creation failed, continuing without linkage', coErr.message)
      } else if (co) {
        activeCheckoutId.value = co.id
        // Keep serial status in sync so map/status views pick up this checkout immediately.
        await supabase
          .schema('app')
          .from('equipment_serials')
          .update({
            status: 'checked-out',
            last_status_change: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', serialRow.id)
        try {
          window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT))
        } catch {}
      }
    }
  } catch (e: any) {
    // Non-fatal — still show shipping modal even if checkout creation fails
    log.warn('checkout creation error:', e?.message)
  }
  showRoadmap.value = true
  showShipping.value = true
}
</script>

<style scoped>
.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.18s cubic-bezier(0.2, 0.8, 0.2, 1.2);
}
.modal-scale-enter-from,
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
</style>

<style scoped>
.veil-enter-active,
.veil-leave-active {
  transition: opacity 0.18s ease;
}
.veil-enter-from,
.veil-leave-to {
  opacity: 0;
}
.modal-pop-enter-active {
  animation: pop-in 0.22s cubic-bezier(0.2, 0.8, 0.2, 1.2);
}
.modal-pop-leave-active {
  animation: pop-out 0.15s ease forwards;
}
@keyframes pop-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
@keyframes pop-out {
  to {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
}
</style>
